<template>
  <div
    class="tw:ml-auto tw:flex tw:flex-nowrap tw:items-center tw:gap-0.5"
    :data-walkthrough-id="`stream-${index}-header-toolbar`"
  >
    <slot name="prependButtonBar" />

    <template v-if="!hideRangeSelector">
      <div v-if="isDesktop">
        <div class="tw:flex tw:items-center">
          <BaseInputDatePicker
            v-model:range="range"
            :allow-all-time="allowAllTime"
            bordered
            class="e2e-date-range"
            close-on-update
            data-testid="date-range-picker"
            :data-walkthrough-id="`stream-${index}-header-toolbar-date-range`"
            limit-to-past-year
            range-selection="custom"
            show-date-input
            show-range-options
            show-time-input
            show-update-button
          />
          <BaseStreemTooltip>
            {{ getLocaleText("mention_stream.select_date_range") }}
          </BaseStreemTooltip>
        </div>
      </div>

      <div v-else>
        <BaseButton
          :aria-label="getLocaleText('mention_stream.select_date_range')"
          :bordered="rangeSelectorActive"
          color="secondary"
          data-testid="date-range-button"
          :data-walkthrough-id="`stream-${index}-header-toolbar-date-range-mobile`"
          flat
          icon="ion-md-time"
          :label="rangeLabel"
          new-design
          @click="rangeSelectorActive = !rangeSelectorActive"
        />
      </div>
    </template>

    <BaseButtonGroup gap="xs">
      <BaseButton
        v-if="showAddExternalItemsButton"
        v-bind="barButtonAttrs"
        :aria-label="getLocaleText('mention_stream.add_external_item_button')"
        data-testid="add-external-item-button"
        :data-walkthrough-id="`stream-${index}-header-toolbar-external-item`"
        icon="insert-external-item"
        @click="startAddExternalItemsWorkflow"
      >
        <BaseStreemTooltip
          anchor="top middle"
          self="bottom right"
        >
          {{ getLocaleText("mention_stream.add_external_item_button") }}
        </BaseStreemTooltip>
      </BaseButton>

      <BaseButton
        v-if="canGenerateStreamReports"
        v-bind="barButtonAttrs"
        :aria-label="getLocaleText('mention_stream.generate_report_button')"
        data-testid="create-report"
        :data-walkthrough-id="`stream-${index}-header-toolbar-create-report`"
        icon="create-report"
        @click="openMentionStreamReport"
      >
        <BaseStreemTooltip
          anchor="top middle"
          self="bottom right"
        >
          {{ getLocaleText("mention_stream.generate_report_button") }}
        </BaseStreemTooltip>
      </BaseButton>

      <BaseButton
        v-if="!hideFiltersSelector && stream.id"
        v-bind="barButtonAttrs"
        :active="filtersSelectorActive"
        :active-color="filtering ? 'activated' : 'secondary'"
        :aria-label="getLocaleText('mention_stream.filter_mentions_button')"
        class="e2e-filter"
        :color="filtering ? 'activated' : 'white'"
        data-testid="filter"
        :data-walkthrough-id="`stream-${index}-header-toolbar-filters`"
        icon="filter"
        @click="emit('filters')"
      >
        <BaseStreemTooltip
          anchor="top middle"
          self="bottom right"
        >
          {{ getLocaleText("mention_stream.filter_mentions_button") }}
        </BaseStreemTooltip>
      </BaseButton>

      <BaseButton
        v-if="!hideEditButton"
        v-bind="barButtonAttrs"
        :aria-label="getLocaleText('mention_stream.edit_mention_stream_button')"
        class="e2e-edit"
        data-testid="edit"
        :data-walkthrough-id="`stream-${index}-header-toolbar-edit`"
        icon="settings"
        @click="openEditStream"
      >
        <BaseStreemTooltip
          anchor="top middle"
          self="bottom right"
        >
          {{ getLocaleText("mention_stream.edit_mention_stream_button") }}
        </BaseStreemTooltip>
      </BaseButton>
    </BaseButtonGroup>

    <slot name="buttonBar" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import { useRouter } from "vue-router";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonGroup,
  BaseInputDatePicker,
  BaseStreemTooltip,
} from "shared/components/base";
import { type ButtonSize } from "shared/components/base/BaseButton";
import { streamTypes } from "shared/constants";
import type DateRange from "shared/helpers/DateRange";
import { safePush } from "shared/helpers/routing";
import { useAddExternalItemsWorkflow } from "shared/modals/addExternalItemsWorkflow";
import features from "shared/services/features";
import Permissions from "shared/services/Permissions";
import { type Stream } from "shared/types";

interface MentionStreamHeaderToolbarButtonAttrs {
  bordered: boolean;
  color: string;
  iconSize: string;
  newDesign: boolean;
  newIcon: boolean;
  size: ButtonSize;
}

interface MentionStreamHeaderToolbarProps {
  allowAllTime?: boolean;
  filtering?: boolean;
  filtersSelectorActive?: boolean;
  groupSlug?: string;
  hideEditButton?: boolean;
  hideFiltersSelector?: boolean;
  hideGenerateReportButton?: boolean;
  hideRangeSelector?: boolean;
  index: number;
  stream: Stream;
}

const props = withDefaults(defineProps<MentionStreamHeaderToolbarProps>(), {
  groupSlug: "",
});

const emit = defineEmits<{
  filters: [];
}>();

const range = defineModel<DateRange>("range");
const rangeSelectorActive = defineModel<boolean>("rangeSelectorActive");

const router = useRouter();
const safeRouterPush = safePush({ router });
const isDesktop = inject<boolean>("isDesktop", false);
const permissions = new Permissions();

const { startAddExternalItemsWorkflow } = useAddExternalItemsWorkflow({
  streamId: props.stream.id,
});

const showAddExternalItemsButton = computed<boolean>(() => {
  if (props.stream.type === streamTypes.bookmarkStream) return false;

  return (
    isDesktop &&
    features.has("has_external_items") &&
    permissions.has("can_insert_external_items")
  );
});

const canGenerateStreamReports = computed<boolean>(
  () =>
    !props.hideGenerateReportButton &&
    features.hasAll(["has_reports", "streams_reports"]) &&
    permissions.has("can_generate_stream_reports")
);

const rangeLabel = computed<string>(() =>
  range.value ? range.value.toString().replace("Last ", "") : ""
);

const barButtonAttrs = computed<MentionStreamHeaderToolbarButtonAttrs>(() => ({
  newDesign: true,
  newIcon: true,
  color: "white",
  size: isDesktop ? "sm" : "md",
  bordered: true,
  iconSize: "lg",
}));

const isBookmarkStream = computed<boolean>(
  () => props.stream.type === streamTypes.bookmarkStream
);

const reportRouteName = computed<string>(() =>
  isBookmarkStream.value ? "bookmark-report" : "mention-stream-report-new"
);

function openEditStream() {
  track("Opened Edit Mention Stream", {
    streamId: props.stream.id,
  });

  safeRouterPush({
    name: "mention-stream-edit",
    params: {
      streamSlug: props.stream.slug,
      groupSlug: props.groupSlug,
    },
  });
}

function openMentionStreamReport() {
  track("Opened Mention Stream Report", {
    streamId: props.stream.id,
  });

  safeRouterPush({
    name: reportRouteName.value,
    params: {
      selectedStreamId: props.stream.id,
      streamSlug: props.stream.slug,
      groupSlug: props.groupSlug,
    },
    props,
  });
}
</script>
